import { GeocodeResult } from "@googlemaps/google-maps-services-js";

export interface GoogleMapsGeocoderContextType {
  isLoaded: boolean;
  lookupAddress: (address: string) => Promise<GeocodeResult[]>;
  geocodeAddress: (address: string) => Promise<GeocodeResult[]>;
  reverseGeocode: (lat: number, lng: number) => Promise<GeocodeResult[]>;
}
  
  // google-maps-context.tsx
  import { createContext, useContext, createSignal, JSX, onMount } from "solid-js";
  import { createStore } from "solid-js/store";
  
  const GoogleMapsGeocoderContext = createContext<GoogleMapsGeocoderContextType>();
  
  export interface GoogleMapsGeocoderProviderProps {
    apiKey: string;
    children: JSX.Element;
  }
  
  export function GoogleMapsGeocoderProvider(props: GoogleMapsGeocoderProviderProps) {
    const [isLoaded, setIsLoaded] = createSignal(false);
    const [geocoder, setGeocoder] = createSignal<google.maps.Geocoder | null>(null);
    const [loadCallbacks, setLoadCallbacks] = createStore<Array<() => void>>([]);
  
    // Initialize the Google Maps API
    onMount(() => {
      loadGoogleMapsApi();
    });
  
    /**
     * Load the Google Maps JavaScript API
     */
    const loadGoogleMapsApi = (): void => {
      // Skip if already loaded or in process of loading
      if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
        checkApiLoaded();
        return;
      }
  
      // Create script element
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${props.apiKey}&libraries=places&callback=googleMapsApiLoaded`;
      script.async = true;
      script.defer = true;
      
      // Define global callback
      (window as any).googleMapsApiLoaded = () => {
        setIsLoaded(true);
        setGeocoder(new google.maps.Geocoder());
        
        // Execute any callbacks waiting for API to load
        loadCallbacks.forEach(callback => callback());
        setLoadCallbacks([]);
      };
      
      // Append script to document
      document.head.appendChild(script);
    };
  
    /**
     * Check if the API is already loaded
     */
    const checkApiLoaded = (): void => {
      if ((window as any).google && (window as any).google.maps) {
        setIsLoaded(true);
        setGeocoder(new google.maps.Geocoder());
      } else {
        // Check again in 100ms
        setTimeout(() => checkApiLoaded(), 100);
      }
    };
  
    /**
     * Ensure the API is loaded before proceeding
     */
    const ensureApiLoaded = (): Promise<void> => {
      return new Promise((resolve) => {
        if (isLoaded() && geocoder()) {
          resolve();
        } else {
          setLoadCallbacks([...loadCallbacks, resolve]);
        }
      });
    };
  
    /**
     * Geocode an address
     */
    const geocodeAddress = async (address: string): Promise<GeocodeResult[]> => {
      await ensureApiLoaded();
      
      return new Promise((resolve, reject) => {
        const geocoderInstance = geocoder();
        if (!geocoderInstance) {
          reject(new Error("Geocoder not initialized"));
          return;
        }
        
        geocoderInstance.geocode({ address }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results) {
            resolve(results as unknown as GeocodeResult[]);
          } else {
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      });
    };
  
    /**
     * Reverse geocode a location
     */
    const reverseGeocode = async (lat: number, lng: number): Promise<GeocodeResult[]> => {
      await ensureApiLoaded();
      
      return new Promise((resolve, reject) => {
        const geocoderInstance = geocoder();
        if (!geocoderInstance) {
          reject(new Error("Geocoder not initialized"));
          return;
        }
        
        const location = { lat, lng };
        
        geocoderInstance.geocode({ location }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results) {
            resolve(results as unknown as GeocodeResult[]);
          } else {
            reject(new Error(`Reverse geocoding failed: ${status}`));
          }
        });
      });
    };
  
    // Helper method to get coordinates from address
    const lookupAddress = async (address: string): Promise<GeocodeResult[]> => {
      try {
        const results = await geocodeAddress(address);
        return results;
      } catch (error) {
        console.error("Error getting coordinates:", error);
        return [];
      }
    };
  
    const contextValue: GoogleMapsGeocoderContextType = {
      isLoaded: isLoaded(),
      lookupAddress,
      geocodeAddress,
      reverseGeocode
    };
  
    return (
      <GoogleMapsGeocoderContext.Provider value={contextValue}>
        {props.children}
      </GoogleMapsGeocoderContext.Provider>
    );
  }
  
  // Custom hook to use the Google Maps context
  export function useGoogleMapsGeocoder() {
    const context = useContext(GoogleMapsGeocoderContext);
    if (!context) {
      throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
    }
    return context;
  }